/**
 * src/components/IncomingOrders/OrderProductsAndSummation/HeaderCell.jsx
 *
 * Table header cell with optional contextual actions (edit and settings).
 * Designed for use in product/order tables where column-level actions
 * are anchored to the header cell.
 */

import PropTypes from "prop-types";
import { useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import { IconButton, TableCell, Tooltip, styled } from "@mui/material";

/**
 * Styled components for HeaderCell
 */

// Root TableCell with hover effect for actions
const HeaderCellRoot = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "columnIsDirty", // don't pass to DOM
})(({ theme, columnIsDirty, align }) => ({
  position: "relative",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "normal",
  textAlign: align || "left",
  verticalAlign: "bottom",
  overflow: "visible",
  userSelect: "none",
  borderRadius: 10,
  transition: "background 120ms ease",
  backgroundColor: columnIsDirty ? "rgba(255,244,229,0.5)" : undefined, // optional future dirty styling
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover .headerActions": {
    opacity: 1,
  },
}));

// Inner wrapper to support hover bridge for actions
const HeaderInner = styled("div", {
  shouldForwardProp: (prop) => prop !== "align",
})(({ align }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent:
    align === "right"
      ? "flex-end"
      : align === "center"
      ? "center"
      : "flex-start",
  "&::after": {
    content: '""',
    position: "absolute",
    right: -60,
    top: 0,
    width: 60,
    height: "100%",
  },
}));

// Label container inside the cell
const HeaderLabel = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

// Actions container (edit/settings)
const HeaderActions = styled("span")({
  zIndex: 5,
  position: "absolute",
  right: -30,
  bottom: 0,
  display: "flex",
  gap: 0,
  opacity: 0,
  transition: "opacity 120ms ease",
  pointerEvents: "auto",
  backgroundColor: "rgba(255,255,255,0.75)",
});

// IconButton wrapper
const HeaderIconBtn = styled(IconButton)({
  padding: 3,
  borderRadius: 6,
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.06)",
  },
});

/**
 * Table header cell with optional contextual actions.
 *
 * Each action is passed as a configuration object via `editAction`
 * and `settingsAction`.
 *
 * When triggered, the action receives:
 *   { anchorEl: HTMLElement, field: string }
 *
 * The component handles hover visibility, positioning and accessibility.
 *
 * @param {Object} props
 * @param {string} props.field - Field identifier for the column.
 * @param {Object} [props.editAction] - Optional edit action configuration.
 * @param {function} props.editAction.onClick - Callback invoked on edit click.
 * @param {React.ReactNode} props.editAction.title - Tooltip title.
 * @param {Object} [props.settingsAction] - Optional settings action configuration.
 * @param {function} props.settingsAction.onClick - Callback invoked on settings click.
 * @param {React.ReactNode} props.settingsAction.title - Tooltip title.
 * @param {boolean} props.children - Optional prop for future dirty-state styling.
 * @param {React.ReactNode} props.children - Header content.
 *
 * @example
 * <HeaderCell
 *   field="price"
 *   editAction={{ onClick: handleEdit, title: "Bulk edit" }}
 * >
 *   Price
 * </HeaderCell>
 */
const HeaderCell = ({
  field,
  editAction,
  settingsAction,
  columnIsDirty = false, // optional prop for future dirty-state styling
  children,
  align,
}) => {
  const cellRef = useRef(null);

  const createAriaLabel = (title) => (typeof title === "string" ? title : undefined);

  return (
    <HeaderCellRoot ref={cellRef} columnIsDirty={columnIsDirty} align={align}>
      <HeaderInner align={align}>
        <HeaderLabel>{children}</HeaderLabel>

        <HeaderActions className="headerActions">
          {editAction?.onClick && (
            <Tooltip title={editAction.title} placement="top" arrow>
              <HeaderIconBtn
                aria-label={createAriaLabel(editAction.title)}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  editAction.onClick({ anchorEl: cellRef.current, field });
                }}
              >
                <EditIcon fontSize="inherit" />
              </HeaderIconBtn>
            </Tooltip>
          )}

          {settingsAction?.onClick && (
            <Tooltip title={settingsAction.title} placement="top" arrow>
              <HeaderIconBtn
                aria-label={createAriaLabel(settingsAction.title)}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  settingsAction.onClick({ anchorEl: cellRef.current, field });
                }}
              >
                <SettingsIcon fontSize="inherit" />
              </HeaderIconBtn>
            </Tooltip>
          )}
        </HeaderActions>
      </HeaderInner>
    </HeaderCellRoot>
  );
};

HeaderCell.propTypes = {
  align: PropTypes.oneOf(["left", "right", "center"]),
  field: PropTypes.string.isRequired,
  editAction: PropTypes.shape({
    onClick: PropTypes.func,
    title: PropTypes.node,
  }),
  settingsAction: PropTypes.shape({
    onClick: PropTypes.func,
    title: PropTypes.node,
  }),
  children: PropTypes.node,
  columnIsDirty: PropTypes.bool, // optional
};

HeaderCell.displayName = "HeaderCell";

export default HeaderCell;
